import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail, Instagram, Facebook, MessageCircle, Loader2 } from 'lucide-react';
import { contactImage, contactInfo, pricingCategories } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../lib/api';

const allTreatments = pricingCategories.flatMap((c) =>
  c.services.map((s) => ({
    label: `${c.title} \u2014 ${s.name} (${s.price})`,
    value: `${c.title} - ${s.name} (${s.price})`,
  }))
);

const Contact = () => {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    treatment: '',
    date: '',
    time: '',
    message: '',
  });

  const buildBody = () =>
`Hi Dawn,

I'd like to book a treatment with Blissfull Mobile Spa. Here are my details:

Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Treatment: ${form.treatment || 'Not sure yet'}
Preferred date: ${form.date || 'Flexible'}
Preferred time: ${form.time || 'Flexible'}

Message: ${form.message}

Thank you!`;

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill in the required fields', description: 'Name, email, and a short message are required.' });
      return;
    }
    setSending(true);
    try {
      const res = await publicApi.post('/booking', form);
      if (res.data.email_sent) {
        toast({ title: 'Booking sent \u2728', description: 'Dawn just received your request. She will confirm shortly.' });
      } else {
        toast({
          title: 'Saved \u2014 opening your email app',
          description: "We saved your request and are opening your mail app as backup so Dawn definitely gets it.",
        });
        const subject = encodeURIComponent(`Booking Request from ${form.name}`);
        const body = encodeURIComponent(buildBody());
        window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
      }
      setForm({ name: '', email: '', phone: '', treatment: '', date: '', time: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try WhatsApp instead.' });
    } finally {
      setSending(false);
    }
  };

  const submitWhatsApp = () => {
    if (!form.name || !form.message) {
      toast({ title: 'Please share a name and message', description: 'So Dawn knows who is booking.' });
      return;
    }
    const msg = encodeURIComponent(buildBody());
    window.open(`https://wa.me/${contactInfo.whatsappIntl}?text=${msg}`, '_blank');
  };

  return (
    <section id="contact" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            BOOK YOUR TREATMENT
          </p>
          <h2 className="text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            Have questions or just want to say hi?
          </h2>
          <p className="text-white/90 mt-4 font-light italic text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Your thoughts, queries, and happy vibes are always welcome. I can&apos;t wait to hear from you.
          </p>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-stretch">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.8 }} className="relative">
            <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl" />
            <img src={contactImage} alt="Blissful spa candles" className="relative w-full h-[380px] object-cover rounded-3xl shadow-2xl" loading="lazy" />

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-white/90">
                <MapPin size={18} className="text-white" />
                <span className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>{contactInfo.location}</span>
              </div>
              <a href={`tel:+27${contactInfo.whatsapp.substring(1)}`} className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                <Phone size={18} className="text-white" />
                <span className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>+27 {contactInfo.whatsapp.substring(1, 3)} {contactInfo.whatsapp.substring(3, 6)} {contactInfo.whatsapp.substring(6)}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                <Mail size={18} className="text-white" />
                <span className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>{contactInfo.email}</span>
              </a>
              <div className="flex items-center gap-4 pt-2">
                <a href="#" className="p-2 rounded-full bg-white/15 hover:bg-white hover:text-rose-500 text-white transition-all duration-300" aria-label="Instagram"><Instagram size={18} /></a>
                <a href="#" className="p-2 rounded-full bg-white/15 hover:bg-white hover:text-rose-500 text-white transition-all duration-300" aria-label="Facebook"><Facebook size={18} /></a>
              </div>
            </div>
          </motion.div>

          <motion.form initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.8 }} onSubmit={submitEmail} className="bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-8 md:p-10 shadow-2xl flex flex-col">
            <div className="space-y-5 flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="NAME" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your name" />
                <Field label="PHONE" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="Contact number" />
              </div>
              <Field label="EMAIL" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" />

              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TREATMENT</label>
                <select value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white py-2 outline-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
                  <option value="" className="text-neutral-800">Choose a treatment (optional)</option>
                  {allTreatments.map((t) => (
                    <option key={t.value} value={t.value} className="text-neutral-800">{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="DATE" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                <Field label="TIME" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
              </div>

              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>MESSAGE</label>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none resize-none"
                  placeholder="Any preferences, allergies, address details..."
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button type="submit" disabled={sending} className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] font-light bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500 disabled:opacity-60"
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                {sending ? (<>SENDING <Loader2 size={14} className="animate-spin" /></>) : (<>SEND BOOKING <Send size={14} /></>)}
              </button>
              <button type="button" onClick={submitWhatsApp} className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] font-light bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500"
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                WHATSAPP <MessageCircle size={14} />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
      placeholder={placeholder} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', colorScheme: 'dark' }} />
  </div>
);

export default Contact;
